// src/components/admin/curveUtils.js
// Mathematical utilities for curve generation and interpolation

/**
 * Linear interpolation between two points
 */
export const lerp = (x1, y1, x2, y2, x) => {
  if (x2 === x1) return y1;
  return y1 + (y2 - y1) * (x - x1) / (x2 - x1);
};

/**
 * Find the margin for a given quantity using linear interpolation
 */
export const getMarginAtQuantity = (controlPoints, quantity) => {
  if (!controlPoints || controlPoints.length === 0) return 0;
  
  // Sort points by quantity
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  
  // Handle edge cases
  if (quantity <= sortedPoints[0].quantity) {
    return sortedPoints[0].margin;
  }
  if (quantity >= sortedPoints[sortedPoints.length - 1].quantity) {
    return sortedPoints[sortedPoints.length - 1].margin;
  }
  
  // Find surrounding points
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const p1 = sortedPoints[i];
    const p2 = sortedPoints[i + 1];
    
    if (quantity >= p1.quantity && quantity <= p2.quantity) {
      return lerp(p1.quantity, p1.margin, p2.quantity, p2.margin, quantity);
    }
  }
  
  return 0;
};

/**
 * Generate dense points for smooth curve visualization and backend consumption
 */
export const generateDensePoints = (controlPoints, options = {}) => {
  const {
    sampleRate = 25,
    smoothing = 'linear'
  } = options;
  
  if (!controlPoints || controlPoints.length < 2) return [];
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  const minQuantity = sortedPoints[0].quantity;
  const maxQuantity = sortedPoints[sortedPoints.length - 1].quantity;
  
  const densePoints = [];
  
  // Generate points at regular intervals
  for (let quantity = minQuantity; quantity <= maxQuantity; quantity += sampleRate) {
    const margin = getMarginAtQuantity(sortedPoints, quantity);
    densePoints.push({ quantity, margin });
  }
  
  // Always include the final point
  if (densePoints[densePoints.length - 1]?.quantity !== maxQuantity) {
    const finalMargin = getMarginAtQuantity(sortedPoints, maxQuantity);
    densePoints.push({ quantity: maxQuantity, margin: finalMargin });
  }
  
  return densePoints;
};

/**
 * Spline interpolation for smoother curves (basic implementation)
 * For production, consider using a library like d3-interpolate
 */
export const generateSplinePoints = (controlPoints, options = {}) => {
  const { resolution = 100 } = options;
  
  if (!controlPoints || controlPoints.length < 3) {
    return generateDensePoints(controlPoints, options);
  }
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  const splinePoints = [];
  
  // Simple Catmull-Rom spline implementation
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const p0 = sortedPoints[Math.max(0, i - 1)];
    const p1 = sortedPoints[i];
    const p2 = sortedPoints[i + 1];
    const p3 = sortedPoints[Math.min(sortedPoints.length - 1, i + 2)];
    
    const segmentPoints = Math.max(1, Math.floor(resolution / sortedPoints.length));
    
    for (let t = 0; t < segmentPoints; t++) {
      const u = t / segmentPoints;
      const quantity = catmullRom(p0.quantity, p1.quantity, p2.quantity, p3.quantity, u);
      const margin = catmullRom(p0.margin, p1.margin, p2.margin, p3.margin, u);
      
      splinePoints.push({ quantity, margin });
    }
  }
  
  return splinePoints;
};

/**
 * Catmull-Rom spline interpolation
 */
const catmullRom = (p0, p1, p2, p3, t) => {
  const t2 = t * t;
  const t3 = t2 * t;
  
  return 0.5 * (
    (2 * p1) +
    (-p0 + p2) * t +
    (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
    (-p0 + 3 * p1 - 3 * p2 + p3) * t3
  );
};

/**
 * Validate control points for business rules
 */
export const validateControlPoints = (controlPoints, options = {}) => {
  const {
    minMargin = 50,
    maxMargin = 1000,
    minQuantity = 1,
    maxQuantity = 10000
  } = options;
  
  const errors = [];
  
  if (!controlPoints || controlPoints.length < 2) {
    errors.push('At least 2 control points are required');
    return { isValid: false, errors };
  }
  
  controlPoints.forEach((point, index) => {
    if (point.quantity < minQuantity) {
      errors.push(`Point ${index + 1}: Quantity ${point.quantity} is below minimum ${minQuantity}`);
    }
    if (point.quantity > maxQuantity) {
      errors.push(`Point ${index + 1}: Quantity ${point.quantity} is above maximum ${maxQuantity}`);
    }
    if (point.margin < minMargin) {
      errors.push(`Point ${index + 1}: Margin ${point.margin}% is below minimum ${minMargin}%`);
    }
    if (point.margin > maxMargin) {
      errors.push(`Point ${index + 1}: Margin ${point.margin}% is above maximum ${maxMargin}%`);
    }
  });
  
  // Check for duplicate quantities
  const quantities = controlPoints.map(p => p.quantity);
  const uniqueQuantities = [...new Set(quantities)];
  if (quantities.length !== uniqueQuantities.length) {
    errors.push('Duplicate quantities found - each point must have a unique quantity');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate sample price using curve data (mock implementation)
 */
export const calculateSamplePrice = (quantity, controlPoints, options = {}) => {
  const {
    basePrice = 10,
    baseMaterialCost = 5,
    baseClickCost = 3,
    jobType = 'letterheads'
  } = options;
  
  const margin = getMarginAtQuantity(controlPoints, quantity);
  const marginMultiplier = 1 + (margin / 100);
  
  // Mock calculation - in reality this would use your actual pricing engine
  const totalBaseCost = (baseMaterialCost + baseClickCost) * quantity;
  const markedUpCost = totalBaseCost * marginMultiplier;
  
  return markedUpCost;
};

/**
 * Advanced curve fitting: Exponential decay fitting
 */
export const fitExponentialCurve = async (controlPoints, options = {}) => {
  const {
    preserveEndpoints = true,
    samplePoints = 8
  } = options;
  
  if (!controlPoints || controlPoints.length < 3) return controlPoints;
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  
  // Simple exponential fitting using least squares
  // Format: margin = minMargin + (maxMargin - minMargin) * exp(-decay * quantity)
  const quantities = sortedPoints.map(p => p.quantity);
  const margins = sortedPoints.map(p => p.margin);
  
  const minQuantity = Math.min(...quantities);
  const maxQuantity = Math.max(...quantities);
  const minMargin = Math.min(...margins);
  const maxMargin = Math.max(...margins);
  
  // Estimate decay rate
  const midIndex = Math.floor(sortedPoints.length / 2);
  const midQuantity = sortedPoints[midIndex].quantity;
  const midMargin = sortedPoints[midIndex].margin;
  
  // Simple exponential decay estimation
  const range = maxMargin - minMargin;
  const decayRate = range > 0 ? Math.log((maxMargin - midMargin) / range) / (midQuantity - minQuantity) : -0.001;
  
  // Generate fitted points
  const fittedPoints = [];
  const step = (maxQuantity - minQuantity) / (samplePoints - 1);
  
  for (let i = 0; i < samplePoints; i++) {
    const quantity = minQuantity + (i * step);
    const margin = minMargin + range * Math.exp(decayRate * (quantity - minQuantity));
    
    fittedPoints.push({
      quantity: Math.round(quantity),
      margin: Math.max(minMargin, Math.min(maxMargin, margin))
    });
  }
  
  // Preserve original endpoints if requested
  if (preserveEndpoints && sortedPoints.length >= 2) {
    fittedPoints[0] = { ...sortedPoints[0] };
    fittedPoints[fittedPoints.length - 1] = { ...sortedPoints[sortedPoints.length - 1] };
  }
  
  return fittedPoints;
};

/**
 * Advanced curve fitting: Polynomial fitting
 */
export const fitPolynomialCurve = async (controlPoints, options = {}) => {
  const {
    degree = 3,
    samplePoints = 8
  } = options;
  
  if (!controlPoints || controlPoints.length < degree + 1) return controlPoints;
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  
  // Simple polynomial fitting using method of least squares
  // This is a basic implementation - for production, consider using a math library
  const quantities = sortedPoints.map(p => p.quantity);
  const margins = sortedPoints.map(p => p.margin);
  
  const minQuantity = Math.min(...quantities);
  const maxQuantity = Math.max(...quantities);
  
  // Normalize quantities for better numerical stability
  const normalizedQuantities = quantities.map(q => (q - minQuantity) / (maxQuantity - minQuantity));
  
  // Build matrix for polynomial fitting (simplified approach)
  const matrix = [];
  const vector = [];
  
  for (let i = 0; i < normalizedQuantities.length; i++) {
    const row = [];
    for (let j = 0; j <= degree; j++) {
      row.push(Math.pow(normalizedQuantities[i], j));
    }
    matrix.push(row);
    vector.push(margins[i]);
  }
  
  // Simple polynomial coefficient estimation (basic implementation)
  const coeffs = solveLinearSystem(matrix, vector, degree + 1);
  
  // Generate fitted points
  const fittedPoints = [];
  const step = 1 / (samplePoints - 1);
  
  for (let i = 0; i < samplePoints; i++) {
    const normalizedQuantity = i * step;
    const quantity = minQuantity + normalizedQuantity * (maxQuantity - minQuantity);
    
    let margin = 0;
    for (let j = 0; j <= degree && j < coeffs.length; j++) {
      margin += coeffs[j] * Math.pow(normalizedQuantity, j);
    }
    
    fittedPoints.push({
      quantity: Math.round(quantity),
      margin: Math.max(0, margin)
    });
  }
  
  return fittedPoints;
};

/**
 * Advanced curve fitting: Spline fitting
 */
export const fitSplineCurve = async (controlPoints, options = {}) => {
  const {
    samplePoints = 8,
    tension = 0.5
  } = options;
  
  if (!controlPoints || controlPoints.length < 3) return controlPoints;
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  
  // Generate key anchor points (simplified spline)
  const keyPoints = generateControlPointsFromData(sortedPoints, Math.min(samplePoints, sortedPoints.length));
  
  // Apply spline interpolation between key points
  const splinePoints = [];
  
  for (let i = 0; i < keyPoints.length - 1; i++) {
    const p0 = keyPoints[Math.max(0, i - 1)];
    const p1 = keyPoints[i];
    const p2 = keyPoints[i + 1];
    const p3 = keyPoints[Math.min(keyPoints.length - 1, i + 2)];
    
    // Add points along the spline segment
    const segments = Math.max(1, Math.floor(samplePoints / keyPoints.length));
    
    for (let t = 0; t < segments; t++) {
      const u = t / segments;
      const quantity = catmullRom(p0.quantity, p1.quantity, p2.quantity, p3.quantity, u);
      const margin = catmullRom(p0.margin, p1.margin, p2.margin, p3.margin, u);
      
      splinePoints.push({
        quantity: Math.round(quantity),
        margin: Math.max(0, margin)
      });
    }
  }
  
  // Add final point
  splinePoints.push({ ...keyPoints[keyPoints.length - 1] });
  
  // Remove duplicates and sort
  const uniquePoints = splinePoints.filter((point, index, array) => 
    index === 0 || point.quantity !== array[index - 1].quantity
  );
  
  return uniquePoints.sort((a, b) => a.quantity - b.quantity);
};

/**
 * Simple linear system solver (Gaussian elimination)
 * Basic implementation for polynomial fitting
 */
const solveLinearSystem = (matrix, vector, size) => {
  const augmented = matrix.map((row, i) => [...row, vector[i]]);
  
  // Forward elimination
  for (let i = 0; i < size - 1; i++) {
    for (let j = i + 1; j < size; j++) {
      if (Math.abs(augmented[i][i]) < 1e-10) continue;
      const factor = augmented[j][i] / augmented[i][i];
      for (let k = i; k <= size; k++) {
        augmented[j][k] -= factor * augmented[i][k];
      }
    }
  }
  
  // Back substitution
  const solution = new Array(size).fill(0);
  for (let i = size - 1; i >= 0; i--) {
    solution[i] = augmented[i][size];
    for (let j = i + 1; j < size; j++) {
      solution[i] -= augmented[i][j] * solution[j];
    }
    if (Math.abs(augmented[i][i]) > 1e-10) {
      solution[i] /= augmented[i][i];
    }
  }
  
  return solution;
};

/**
 * Enhanced smooth control points with better algorithm
 */
export const smoothControlPoints = (controlPoints, tolerance = 5) => {
  if (controlPoints.length <= 3) return controlPoints;
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
  const smoothedPoints = [sortedPoints[0]]; // Always keep first point
  
  for (let i = 1; i < sortedPoints.length - 1; i++) {
    const prev = smoothedPoints[smoothedPoints.length - 1]; // Last kept point
    const current = sortedPoints[i];
    const next = sortedPoints[i + 1];
    
    // Calculate expected margin if this point didn't exist
    const expectedMargin = lerp(prev.quantity, prev.margin, next.quantity, next.margin, current.quantity);
    const difference = Math.abs(current.margin - expectedMargin);
    
    // Keep point if it significantly affects the curve shape
    if (difference > tolerance) {
      smoothedPoints.push(current);
    }
  }
  
  smoothedPoints.push(sortedPoints[sortedPoints.length - 1]); // Always keep last point
  
  return smoothedPoints;
};

/**
 * Generate suggested control points from dense data
 */
export const generateControlPointsFromData = (densePoints, maxPoints = 10) => {
  if (!densePoints || densePoints.length <= maxPoints) {
    return densePoints;
  }
  
  const sortedPoints = [...densePoints].sort((a, b) => a.quantity - b.quantity);
  const step = Math.floor(sortedPoints.length / (maxPoints - 1));
  const controlPoints = [];
  
  // Always include first point
  controlPoints.push(sortedPoints[0]);
  
  // Sample points at regular intervals
  for (let i = step; i < sortedPoints.length - step; i += step) {
    controlPoints.push(sortedPoints[i]);
  }
  
  // Always include last point
  controlPoints.push(sortedPoints[sortedPoints.length - 1]);
  
  return controlPoints;
};

/**
 * Shift all control point margins by a fixed amount or percentage.
 * Ensures margins stay within reasonable bounds.
 * @param {Array<Object>} controlPoints - The array of control points.
 * @param {number} shiftValue - The value to shift by (positive for up, negative for down if percentage).
 * @param {string} shiftType - 'percentage' or 'fixed'.
 * @param {Object} options - Optional parameters for min/max margin.
 * @returns {Array<Object>} A new array of control points with shifted margins.
 */
export const shiftMargins = (controlPoints, shiftValue, shiftType = 'percentage', options = {}) => {
  if (!controlPoints || controlPoints.length === 0) return [];
  if (typeof shiftValue !== 'number') return controlPoints;

  // Use defaults from validateControlPoints if not overridden in options
  const {
    minMargin = 50, // Default from validateControlPoints
    maxMargin = 1000 // Default from validateControlPoints
  } = options;

  return controlPoints.map(point => {
    let newMargin;
    if (shiftType === 'percentage') {
      newMargin = point.margin * (1 + shiftValue / 100);
    } else if (shiftType === 'fixed') {
      newMargin = point.margin + shiftValue;
    } else {
      return { ...point }; // Invalid shift type, return original point
    }
    const constrainedMargin = Math.max(minMargin, Math.min(maxMargin, newMargin));
    return {
      ...point,
      margin: parseFloat(constrainedMargin.toFixed(1))
    };
  });
};